/**
 * MARKETPLACE. The super-app's investment surface (new, extends FR-U-TOK and the investment model).
 *
 * Where a verified user meets tokenized real-world assets. Every listing is an interest in a Special
 * Purpose Trust that legally holds the asset (the Trust Entity layer, ADR 0005): you are never buying
 * "the land", you are buying a fraction of the trust that owns it. That distinction is surfaced on
 * every card, not buried, because it is the whole legal basis of the thing.
 *
 * Browsing is open to any verified user; committing money is gated downstream by tier and the
 * compliance spine, exactly like a payment. This screen shows and routes, it does not move value.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Listing, type RiskBand, type SecondaryListing } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';
import { WatchButton } from '@/components/watch-button';
import { formatMinor, formatUnits } from '@/lib/tokenization';

// tone -> a literal gradient class pair (literal, so Tailwind does not purge it). No external images.
const TONE: Record<string, string> = {
  emerald: 'from-emerald-500 to-emerald-700',
  amber: 'from-amber-400 to-amber-600',
  lime: 'from-lime-500 to-lime-700',
  sky: 'from-sky-500 to-sky-700',
  violet: 'from-violet-500 to-violet-700',
  teal: 'from-teal-500 to-teal-700',
};

const RISK: Record<RiskBand, { label: string; cls: string }> = {
  low: { label: 'Lower risk', cls: 'bg-accent/15 text-accent' },
  medium: { label: 'Medium risk', cls: 'bg-warning/15 text-warning' },
  high: { label: 'Higher risk', cls: 'bg-danger/10 text-danger' },
};

export default function Marketplace() {
  const { data, loading, error, reload } = useResource(() => api.marketplace(), []);

  // THE SECONDARY MARKET, in the SAME marketplace. Holders reselling part of a tokenized asset they
  // already own (migration 0055). It is a section here rather than a screen of its own because a
  // buyer looking for a fraction of a building does not care whether they are buying it from the
  // trust's inventory or from another holder, and a second marketplace would have been a second
  // order book to keep in step with this one.
  const resale = useResource(() => api.tokenization.market(), []);

  const [category, setCategory] = useState('All');

  const listings = useMemo(() => {
    if (!data) return [];
    return category === 'All'
      ? data.listings
      : data.listings.filter((l) => l.category === category);
  }, [data, category]);

  const featured = data?.listings.slice().sort((a, b) => b.yieldPct - a.yieldPct)[0];

  if (loading) return <LoadingGrid />;
  if (error)
    return <ErrorState body="We could not load the marketplace just now." onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-ink">Marketplace</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            Invest in tokenized real-world assets, from KES 1,000. Each one is held in a regulated
            trust.
          </p>
        </div>
        <Link
          to="/watchlist"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-field border border-border bg-surface px-3 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M12 20s-7-4.35-9.2-8.5C1.3 8.6 2.6 5.5 5.7 5.5c1.9 0 3.2 1.1 4.3 2.6 1.1-1.5 2.4-2.6 4.3-2.6 3.1 0 4.4 3.1 2.9 6-2.2 4.15-9.2 8.5-9.2 8.5Z"
            />
          </svg>
          <span className="hidden sm:inline">Watchlist</span>
        </Link>
      </header>

      {/* FEATURED. The highest-yield listing, given the width to sell itself. */}
      {featured && category === 'All' ? (
        <Reveal>
          <Link
            to={`/marketplace/${featured.id}`}
            className={`relative flex min-h-[180px] flex-col justify-end overflow-hidden rounded-xl bg-gradient-to-br ${TONE[featured.tone] ?? TONE.emerald} p-5 text-white shadow-lifted transition hover:shadow-popover`}
          >
            {featured.image ? (
              <img
                src={featured.image}
                alt=""
                aria-hidden
                loading="lazy"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover"
              />
            ) : null}
            {/* The scrim, not decoration. The title and the yield sit on top of the artwork and have
                to stay legible over whichever asset class it happens to be. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/15 blur-2xl"
            />
            <span className="relative w-fit rounded-pill bg-white/20 px-2.5 py-0.5 text-micro font-medium backdrop-blur">
              Featured &middot; {featured.category}
            </span>
            <h2 className="relative mt-3 text-2xl font-semibold tracking-tight">
              {featured.title}
            </h2>
            <div className="relative mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/85">
              <span className="tabular text-base font-semibold text-white">
                {featured.yieldPct}%{' '}
                <span className="font-normal text-white/80">projected yield</span>
              </span>
              <span>{featured.location}</span>
              <span>{featured.fundedPct}% funded</span>
            </div>
          </Link>
        </Reveal>
      ) : null}

      {/* CATEGORY FILTER. Scrolls horizontally on mobile, wraps on desktop. */}
      <div className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0">
        <div className="flex w-max gap-2 lg:w-full lg:flex-wrap">
          {data.categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`whitespace-nowrap rounded-pill border px-3.5 py-1.5 text-sm transition ${
                category === c
                  ? 'border-primary bg-primary text-white'
                  : 'border-border bg-surface text-ink-muted hover:border-primary/40 hover:text-ink'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* LISTINGS GRID. 1 column on mobile, 2 at md, 3 at xl. */}
      {listings.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-8 text-center text-sm text-ink-muted">
          Nothing listed in {category} yet. Check back soon.
        </p>
      ) : (
        <Stagger className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {listings.map((l) => (
            <StaggerItem key={l.id}>
              <ListingCard listing={l} />
            </StaggerItem>
          ))}
        </Stagger>
      )}

      {/* RESALE. Tokens held by other participants, offered on the same marketplace. */}
      {category === 'All' && (resale.data?.length ?? 0) > 0 ? (
        <section>
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-section text-ink">Resale</h2>
            <p className="text-micro text-ink-subtle">Sold by holders, not by the trust</p>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            Part of a tokenized asset, offered by somebody who already holds it. The underlying
            asset and the entity that holds it are the same as on the original issue.
          </p>
          <Stagger className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {resale.data!.map((l) => (
              <StaggerItem key={l.listing_id}>
                <ResaleCard listing={l} />
              </StaggerItem>
            ))}
          </Stagger>
        </section>
      ) : null}
    </div>
  );
}

/**
 * A resale listing.
 *
 * The card leads with the UNDERLYING ASSET, not with the ticker. "GVA-RE, 2,000 available" tells a
 * buyer nothing about what they would own; "Green Valley Apartments, Kileleshwa, held by Green Valley
 * Holdings Ltd" is the thing they are actually buying a fraction of. That ordering is the
 * requirement, not a layout preference.
 */
function ResaleCard({ listing: l }: { listing: SecondaryListing }) {
  return (
    <article className="flex h-full flex-col rounded-card border border-border bg-surface p-4 shadow-card transition hover:-translate-y-0.5 hover:shadow-lifted">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            {l.underlying.description ?? l.token_name ?? l.symbol}
          </p>
          <p className="truncate text-micro text-ink-subtle">
            {l.symbol}
            {l.underlying.location ? ` · ${l.underlying.location}` : ''}
          </p>
        </div>
        <span className="shrink-0 rounded-pill bg-surface-sunken px-2 py-0.5 text-nano font-medium text-ink-muted">
          Resale
        </span>
      </div>

      <dl className="mt-3 space-y-1.5 border-t border-border pt-3">
        <Fact
          k="Price per token"
          v={`${l.quote_asset} ${formatMinor(l.price, l.quote_asset)}`}
          strong
        />
        <Fact k="Available" v={`${formatUnits(l.units_available)} tokens`} />
        <Fact k="Smallest lot" v={`${formatUnits(l.minimum)} tokens`} />
        {l.underlying.valuation ? (
          <Fact
            k="Asset valued at"
            v={`${l.underlying.valuation_asset ?? 'KES'} ${formatMinor(
              l.underlying.valuation,
              l.underlying.valuation_asset ?? 'KES',
            )}`}
          />
        ) : null}
        {l.underlying.held_by ? <Fact k="Held by" v={l.underlying.held_by} /> : null}
      </dl>

      {l.note ? <p className="mt-2 text-micro text-ink-muted">{l.note}</p> : null}

      {/* Deliberately NOT a Buy button. Taking a listing runs through the trade room and the escrow
          path, and that flow is not built for token listings yet. A button that opened nothing would
          be worse than saying so. */}
      <p className="mt-3 rounded-field bg-surface-sunken px-3 py-2 text-nano text-ink-subtle">
        Buying a resale lot settles through the peer-to-peer trade room, which is not open for
        tokenized assets yet.
      </p>
    </article>
  );
}

function Fact({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-micro text-ink-subtle">{k}</dt>
      <dd
        className={`tabular truncate text-micro ${strong ? 'font-semibold text-ink' : 'text-ink-muted'}`}
      >
        {v}
      </dd>
    </div>
  );
}

function ListingCard({ listing: l }: { listing: Listing }) {
  const risk = RISK[l.risk];
  return (
    <Link
      to={`/marketplace/${l.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card transition hover:-translate-y-0.5 hover:shadow-lifted"
    >
      {/* Card header. The artwork is same-origin and the gradient stays underneath it, so a missing
          file degrades to the old look rather than to an empty frame. Nothing loads from a CDN. */}
      <div className={`relative h-28 bg-gradient-to-br ${TONE[l.tone] ?? TONE.emerald} p-3`}>
        {l.image ? (
          <img
            src={l.image}
            alt=""
            aria-hidden
            loading="lazy"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
          />
        ) : null}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/25"
        />
        <div className="relative flex items-start justify-between">
          <span className="rounded-pill bg-white/20 px-2 py-0.5 text-micro font-medium text-white backdrop-blur">
            {l.category}
          </span>
          <span
            className={`rounded-pill bg-white/90 px-2 py-0.5 text-micro font-medium ${risk.cls.split(' ').find((c) => c.startsWith('text-'))}`}
          >
            {risk.label}
          </span>
        </div>
        <p className="absolute bottom-3 left-3 text-sm font-medium text-white drop-shadow">
          {l.location}
        </p>
        <div className="absolute bottom-2 right-2">
          <WatchButton id={l.id} onSurface />
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-semibold text-ink">{l.title}</h3>
        <p className="mt-0.5 text-xs text-ink-subtle">Held in {l.trust}</p>

        <div className="mt-3 flex items-baseline justify-between">
          <span className="tabular text-lg font-semibold text-brand">{l.yieldPct}%</span>
          <span className="text-xs text-ink-muted">projected annual yield</span>
        </div>

        {/* Funding progress. */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-ink-muted">
            <span>{l.fundedPct}% funded</span>
            <span className="tabular">{l.investors} investors</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
            <div className="h-full rounded-full bg-accent" style={{ width: `${l.fundedPct}%` }} />
          </div>
        </div>

        <div className="mt-4 flex items-end justify-between border-t border-border pt-3">
          <div>
            <p className="text-micro text-ink-subtle">From</p>
            <p className="tabular text-sm font-medium text-ink">KES {l.minInvest}</p>
          </div>
          <span className="rounded-field bg-primary px-3 py-1.5 text-sm font-medium text-white transition group-hover:bg-primary-light">
            View
          </span>
        </div>
      </div>
    </Link>
  );
}

function LoadingGrid() {
  return (
    <div className="space-y-6">
      <LoadingCard lines={2} />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <LoadingCard key={i} lines={3} />
        ))}
      </div>
    </div>
  );
}
